type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
)

/** Avoid reload loops if controllerchange fires more than once before navigation completes. */
let refreshingForControllerChange = false
let controllerChangeListenerAttached = false
let updateCheckListenersAttached = false
let onlineListenersAttached = false

/** Waiting worker discovered while offline — apply SKIP_WAITING once back online. */
let pendingUpdateRegistration: ServiceWorkerRegistration | null = null
/** controllerchange fired while offline — reload once connectivity returns. */
let reloadPendingAfterOnline = false
let activeRegistration: ServiceWorkerRegistration | null = null

const UPDATE_CHECK_THROTTLE_MS = 60_000
let lastUpdateCheckAt = 0

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine
}

function reloadForServiceWorkerUpdate() {
  if (refreshingForControllerChange) return
  refreshingForControllerChange = true
  window.location.reload()
}

/** Tell a waiting service worker to activate (deferred when offline). */
export function applyWaitingServiceWorker(registration: ServiceWorkerRegistration) {
  if (!registration.waiting) return

  if (!isOnline()) {
    pendingUpdateRegistration = registration
    listenForOnlineServiceWorkerApply()
    return
  }

  registration.waiting.postMessage({ type: 'SKIP_WAITING' })
}

function listenForControllerChangeReload() {
  if (controllerChangeListenerAttached || !('serviceWorker' in navigator)) return
  controllerChangeListenerAttached = true

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshingForControllerChange) return

    if (!isOnline()) {
      reloadPendingAfterOnline = true
      listenForOnlineServiceWorkerApply()
      return
    }

    reloadForServiceWorkerUpdate()
  })
}

function listenForOnlineServiceWorkerApply() {
  if (onlineListenersAttached) return
  onlineListenersAttached = true

  window.addEventListener('online', () => {
    void flushPendingServiceWorkerUpdate()
  })
}

async function flushPendingServiceWorkerUpdate() {
  if (!isOnline()) return

  const registration = pendingUpdateRegistration || activeRegistration
  pendingUpdateRegistration = null

  if (registration) {
    try {
      await registration.update()
    } catch {
      // Ignore — transient network or SW unavailable
    }

    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  if (reloadPendingAfterOnline) {
    reloadPendingAfterOnline = false
    // Defer one tick so controllerchange can fire after skipWaiting when needed.
    window.setTimeout(() => {
      if (refreshingForControllerChange) return
      reloadForServiceWorkerUpdate()
    }, 0)
  }
}

function checkForUpdate(registration: ServiceWorkerRegistration, force = false) {
  if (!isOnline()) return

  const now = Date.now()
  if (!force && now - lastUpdateCheckAt < UPDATE_CHECK_THROTTLE_MS) return
  lastUpdateCheckAt = now

  registration.update().catch(() => {
    // Ignore — offline or SW temporarily unavailable
  })
}

function listenForUpdateChecks(registration: ServiceWorkerRegistration) {
  if (updateCheckListenersAttached) return
  updateCheckListenersAttached = true
  activeRegistration = registration

  listenForOnlineServiceWorkerApply()

  const throttledCheck = () => checkForUpdate(registration)

  window.addEventListener('focus', throttledCheck)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') throttledCheck()
  })

  window.addEventListener('online', () => {
    lastUpdateCheckAt = 0
    checkForUpdate(registration, true)
    void flushPendingServiceWorkerUpdate()
  })
}

export function register(config?: Config) {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
  if (publicUrl.origin !== window.location.origin) {
    return
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config)
    } else {
      registerValidSW(swUrl, config)
    }
  })
}

function registerValidSW(swUrl: string, config?: Config) {
  listenForControllerChangeReload()

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      activeRegistration = registration
      listenForUpdateChecks(registration)

      // Tab left open across a deploy: waiting worker may already exist
      if (registration.waiting && navigator.serviceWorker.controller) {
        if (config?.onUpdate) {
          config.onUpdate(registration)
        } else {
          applyWaitingServiceWorker(registration)
        }
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (!installingWorker) return

        installingWorker.onstatechange = () => {
          if (installingWorker.state !== 'installed') return

          if (navigator.serviceWorker.controller) {
            if (config?.onUpdate) {
              config.onUpdate(registration)
            } else {
              applyWaitingServiceWorker(registration)
            }
          } else if (config?.onSuccess) {
            config.onSuccess(registration)
          }
        }
      }
    })
    .catch(() => {
      // Registration failed — app still works online without SW
    })
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type')
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload()
          })
        })
      } else {
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      // Offline on localhost — skip SW
    })
}

export function unregister() {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.unregister()
    })
    .catch(() => {})
}
